import { type CHCameraId, type SiteId } from '@solinkcorp/api-types';
import { getLocalTimeZone } from '@solinkcorp/utils';
import { ALL_LOCATIONS, type HistogramRange } from '@solinkcorp/vd-common';
import { type CameraSelectMode, type SpotCheckMode } from 'actions/filterActions';
import moment from 'moment';
import { EVENTS_BLACK_OUT_WINDOW_IN_MINUTES, GET_LOCATION_LIST } from 'vd-constants';
import { makeImmerReducer } from './utils';

/**
 * This is a reducer which keeps track of global filter state used across the
 * entire application (the "date" and "location" selector in the top left, and
 * the "histogram range" which is used in multiple places.
 *
 * Note that all values from this reducer are persisted in local storage and
 * reloaded when the app starts up.
 */
export type FilterState = {
  /**
   * The "filter date" shown in the upper left hand corner of the Events, Clips,
   * Video, and Discover pages.  This is also used to drive the cursor in the
   * cursor in the histogram on the events page.
   */
  date: number;
  locationIds: SiteId[];
  locationTags: string[];
  range: HistogramRange;
  cameraGroupId?: string | null;
  timezoneId: string;
  selectedCameraIds: CHCameraId[];
  selectedLocationId?: string | null;
  cameraSelectMode: CameraSelectMode;
  spotCheckMode?: SpotCheckMode;
  spotCheckTitle?: string;
};

export const initialState: FilterState = {
  date: getSafeFilterDateTime(Date.now()),
  locationIds: [ALL_LOCATIONS as SiteId],
  locationTags: [],
  range: 'Day',
  cameraGroupId: null,
  timezoneId: getLocalTimeZone(),
  selectedCameraIds: [],
  selectedLocationId: null,
  cameraSelectMode: null,
  spotCheckMode: 'scheduled',
  spotCheckTitle: '',
};

export const filterReducer = makeImmerReducer(initialState, (draft, action) => {
  switch (action.type) {
    case 'FILTER_SET_DATE':
      draft.date = action.date;
      break;

    case 'FILTER_SET_RANGE':
      draft.range = action.range;
      break;

    case 'FILTER_SET_CAMERA_GROUP':
      draft.cameraGroupId = action.cameraGroupId;
      break;

    case 'RESET_CAMERA_GROUP':
      draft.cameraGroupId = '';
      break;

    case 'FILTER_SET_LOCATIONS':
      draft.locationIds = action.locationIds;
      break;

    case 'FILTER_SET_LOCATION_TAGS':
      draft.locationTags = action.locationTags;
      break;

    /**
     * The filters are optional so they will be undefined if not passed in, in which case we want to keep the existing state.
     */
    case 'FILTER_SET':
      return {
        date: action.date ?? draft.date,
        locationIds: action.locationIds ?? draft.locationIds,
        locationTags: action.locationTags ?? draft.locationTags,
        range: action.range ?? draft.range,
        timezoneId: action.timezoneId ?? draft.timezoneId ?? getLocalTimeZone(),
        selectedCameraIds: action.selectedCameraIds ?? draft.selectedCameraIds,
        cameraGroupId:
          action.cameraGroupId === undefined ? draft.cameraGroupId : action.cameraGroupId,
        selectedLocationId:
          action.selectedLocationId === undefined
            ? draft.selectedLocationId
            : action.selectedLocationId,
        cameraSelectMode:
          action.cameraSelectMode === undefined ? draft.cameraSelectMode : action.cameraSelectMode,
        spotCheckMode:
          action.spotCheckMode === undefined ? draft.spotCheckMode : action.spotCheckMode,
        spotCheckTitle: action.spotCheckTitle ?? draft.spotCheckTitle,
      };

    case 'SET_TIMEZONE_ID':
      draft.timezoneId = action.timezoneId;
      break;

    case 'SELECT_FILTER':
      // When a report is selected, if the range is day, set the date filter to the end of the day,
      // so that these Events will be displayed regardless of when they occured in the day.
      if (action.selectedReport && draft.range === 'Day') {
        draft.date = moment(draft.date).endOf('day').valueOf();
      }
      break;

    case GET_LOCATION_LIST: {
      const { locations } = action;

      if (locations.length === 1) {
        draft.locationIds = [locations[0].id];
      }
    }
  }
});

/**
 * Returns a filter date `Date` object suitable for using as the newest point in time in which
 * to fetch events backwards from. If the date parameter is older than 10 minutes ago, take
 * that date exactly as is. If the date is less than 10 minutes ago, make it 10 minutes ago
 * unless it crosses back to the previous day, in which case just use the start of the same day
 * (this seems weird but that is how the old function worked).
 *
 * The reason for function is because we want to give time for event thumbnails to be processed
 * and made available which is worse case about 10 minutes behind the events returned by the API.
 *
 * @return Date suitable to be used for Filter
 */
function getSafeFilterDateTime(proposedFilterDate: number) {
  const now = moment();
  const minsBetweenNowAndDate = moment(now).diff(proposedFilterDate, 'minutes');

  if (minsBetweenNowAndDate <= EVENTS_BLACK_OUT_WINDOW_IN_MINUTES) {
    let adjustedFilterDate = moment(now).subtract(EVENTS_BLACK_OUT_WINDOW_IN_MINUTES, 'minutes');
    if (!adjustedFilterDate.isSame(now, 'day')) {
      adjustedFilterDate = now;
    }
    return adjustedFilterDate.valueOf();
  }
  return proposedFilterDate;
}
